// src/components/ResponsiveCarousel.js
import React from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useMediaQuery, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { loadImages } from '../utils/loadImages';

// Carga todas las imágenes de la carpeta 'carousel'
const images = loadImages(
  require.context('../assets/img/carousel', false, /\.(jpg|jpeg|png|gif)$/)
);

const ResponsiveCarousel = () => {
  // Breakpoints
  const isLargeScreen = useMediaQuery('(min-width:1200px)');
  const isMediumScreen = useMediaQuery('(min-width:600px) and (max-width:1199px)');
  const itemsPerPage = isLargeScreen ? 3 : isMediumScreen ? 2 : 1;

  return (
    <div style={containerStyle}>
      {/* Estilos adicionales específicos del carrusel */}
      <style>{customCss}</style>

      <Typography
        className="custom-title"
        variant="h4"
        align="center"
        style={titleStyle}
      >
        Nuestros Trabajos
      </Typography>

      <Carousel
        showArrows
        showThumbs={false}
        infiniteLoop
        autoPlay
        interval={3500}
        swipeable
        dynamicHeight={false}
        emulateTouch
        showStatus={false}
        showIndicators
        centerMode
        centerSlidePercentage={100 / itemsPerPage}
        transitionTime={600}
        renderArrowPrev={(onClickHandler, hasPrev, label) =>
          hasPrev && (
            <button
              type="button"
              onClick={onClickHandler}
              title={label}
              style={arrowStylesPrev}
            >
              <ArrowBackIos fontSize="small" />
            </button>
          )
        }
        renderArrowNext={(onClickHandler, hasNext, label) =>
          hasNext && (
            <button
              type="button"
              onClick={onClickHandler}
              title={label}
              style={arrowStylesNext}
            >
              <ArrowForwardIos fontSize="small" />
            </button>
          )
        }
      >
        {images.map((src, index) => (
          <div key={index} className="slideShell">
            <figure className="slideCard">
              <img src={src} alt={`carousel-item-${index}`} className="slideImage" />
              <div className="slideVignette" />
            </figure>
          </div>
        ))}
      </Carousel>
    </div>
  );
};

/* ======================
   Estilos
   ====================== */

// Contenedor general con fondo claro + textura sutil
const containerStyle = {
  position: 'relative',
  padding: '0 28px 30px',
  background:
    'linear-gradient(180deg, #f7f7f9 0%, #f0efe9 60%, #e9e6dc 100%)',
  backgroundImage:
    'radial-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(180deg, #f7f7f9 0%, #f0efe9 60%, #e9e6dc 100%)',
  backgroundSize: '12px 12px, 100% 100%',
  borderRadius: 24,
};

const titleStyle = {
  marginTop: '1.25rem',
  marginBottom: '1.25rem',
  fontWeight: 900,
  textTransform: 'uppercase',
  color: '#EABE2A',
  letterSpacing: '.05em',
};

// Flechas con 3D/glass y dorado
const baseArrow = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'linear-gradient(180deg, rgba(255,255,255,.18), rgba(0,0,0,.35))',
  borderRadius: '999px',
  border: '1px solid rgba(234,190,42,.45)',
  boxShadow:
    '0 2px 0 rgba(255,255,255,.3) inset, 0 10px 24px rgba(0,0,0,.35), 0 0 0 3px rgba(234,190,42,.12)',
  zIndex: 3,
  color: '#fff',
  cursor: 'pointer',
  padding: 10,
  width: 44,
  height: 44,
  display: 'grid',
  placeItems: 'center',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  transition: 'transform .2s ease, box-shadow .25s ease, background .25s ease',
};
const arrowStylesPrev = { ...baseArrow, left: 6 };
const arrowStylesNext = { ...baseArrow, right: 6 };

/* 
  CSS-in-JS para tunear el carrusel y aplicar efecto 3D:
  - Tarjeta con borde dorado suave, sombras en capas y “tilt”
  - Imagen con cover, altura consistente y zoom sutil al hover
  - La slide seleccionada escala un poco más para dar profundidad
*/
const customCss = `
/* Altura adaptativa de las diapositivas (mobile/desktop) */
.carousel .slide { background: transparent; }
.slideShell { padding: 10px; }

.slideCard {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  height: clamp(48vh, 62vh, 72vh);
  display: block;
  margin: 0;
  background: #0e0e0e;
  transform: perspective(1200px) translateZ(0) rotateX(0) rotateY(0);
  transition: transform .28s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease, filter .3s ease;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.06) inset,
    0 0 0 1px rgba(218,165,32,0.18),
    0 14px 24px rgba(0,0,0,0.45),
    0 36px 72px rgba(0,0,0,0.35);
}

/* Borde/halo dorado sutil */
.slideCard::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: 22px;
  background:
    linear-gradient(120deg, rgba(218,165,32,0.18) 0%, rgba(218,165,32,0) 35%, rgba(218,165,32,0) 65%, rgba(218,165,32,0.18) 100%);
  mix-blend-mode: soft-light;
}

/* Imagen */
.slideImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  display: block;
  transform: scale(1);
  transition: transform .6s cubic-bezier(.2,.8,.2,1), filter .4s ease;
  will-change: transform;
}

/* Vignette para mejorar lectura y dar profundidad */
.slideVignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(120% 80% at 50% 85%, rgba(0,0,0,.38) 0%, rgba(0,0,0,.15) 38%, rgba(0,0,0,0) 58%),
    linear-gradient(to bottom, rgba(0,0,0,0) 55%, rgba(0,0,0,.35) 100%);
}

/* Hover 3D: leve tilt y zoom */
.slideCard:hover {
  transform: perspective(1200px) translateY(-6px) scale(1.01) rotateX(.6deg) rotateY(.6deg);
  box-shadow:
    0 1px 0 rgba(255,255,255,0.06) inset,
    0 0 0 1px rgba(218,165,32,0.32),
    0 18px 30px rgba(0,0,0,0.55),
    0 44px 88px rgba(0,0,0,0.45);
}
.slideCard:hover .slideImage { transform: scale(1.06); }

/* La slide central (seleccionada) se ve un poco más grande */
.carousel .slide.selected .slideCard {
  transform: perspective(1200px) translateY(-4px) scale(1.015);
}
.carousel .slide.selected .slideImage { transform: scale(1.035); }

/* Indicadores (puntos) personalizados */
.carousel .control-dots .dot {
  box-shadow: none;
  background: rgba(0,0,0,.22);
  border: 1px solid rgba(234,190,42,.5);
  width: 9px; height: 9px;
  margin: 0 4px;
  transition: transform .2s ease, background .2s ease;
}
.carousel .control-dots .dot.selected {
  background: #EABE2A;
  transform: scale(1.25);
}

/* Hover en flechas */
button[title="previous"], button[title="next"] {
  outline: none;
}
button[title="previous"]:hover, button[title="next"]:hover {
  transform: translateY(-50%) scale(1.04);
  box-shadow:
    0 2px 0 rgba(255,255,255,.3) inset,
    0 14px 28px rgba(0,0,0,.45),
    0 0 0 4px rgba(234,190,42,.18);
}
`;

export default ResponsiveCarousel;
